// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@awc/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Children, FC, useState } from "react";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    includeRightAction?: boolean;
    [key: string]: any;
}
interface OwnProps {
    Image: React.FC;
    Description: React.FC;
    Price: React.FC;
    Qty: React.FC;
    Total: React.FC;
    Remove: React.FC;
    Actions: React.FC;
    Notes: React.FC;
}
export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    rightSection?: InjectableCss;
    infoSection?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    actions?: InjectableCss;
    notes?: InjectableCss;
    remove?: InjectableCss;
}

export const cartlineContainerStyles: CartLineContainerStyles = {};

const names = {
    Image: "Image",
    Description: "Description",
    Price: "Price",
    Qty: "Qty",
    Total: "Total",
    Remove: "Remove",
    Actions: "Actions",
    Notes: "Notes",
};

const Image: FC = () => null;
const Description: FC = () => null;
const Price: FC = () => null;
const Qty: FC = () => null;
const Total: FC = () => null;
const Remove: FC = () => null;
const Actions: FC = () => null;
const Notes: FC = () => null;

const CartLineContainer: React.FC<CartLineContainerProps> & OwnProps = ({
    children,
    extendedStyles,
    includeRightAction = false,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));

    const { Image, Description, Price, Qty, Total, Remove, Actions, Notes } = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement[];
        const MappedChilds = childrenArray.reduce((results, current) => {
            const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
            if (currentName) {
                results[currentName] = current;
            }
            return results;
        }, {} as Record<string, React.ReactElement>);
        const slots = Object.entries(names).reduce((results, [key, value], idx) => {
            results[value] = MappedChilds[value] || null;
            return results;
        }, {} as Record<string, React.ReactElement | null>);

        return slots;
    }, [children]);

    return (
        <StyledWrapper {...styles.container} {...otherProps} {...{ includeRightAction }}>
            <StyledWrapper {...styles.image} className="image-section">
                {Image?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.description} className="description-section">
                {Description?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.price} className="price-section">
                {Price?.props?.children}
            </StyledWrapper>

            {/* Add to List */}
            {Actions?.props?.children && (
                <StyledWrapper {...styles.actions} className="actions-section">
                    {Actions?.props?.children}
                </StyledWrapper>
            )}
            {Remove?.props?.children && (
                <StyledWrapper {...styles.remove} className="remove-section" {...{ includeRightAction }}>
                    {Remove?.props?.children}
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.qty} className="qty-section">
                {Qty?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.total} className="total-section">
                {Total?.props?.children}
            </StyledWrapper>

            {Notes?.props?.children && (
                <StyledWrapper {...styles.notes} className="notes-section">
                    {Notes?.props?.children}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Description = Description;
CartLineContainer.Price = Price;
CartLineContainer.Qty = Qty;
CartLineContainer.Total = Total;
CartLineContainer.Remove = Remove;
CartLineContainer.Actions = Actions;
CartLineContainer.Notes = Notes;

CartLineContainer.Image.displayName = names.Image;
CartLineContainer.Description.displayName = names.Description;
CartLineContainer.Price.displayName = names.Price;
CartLineContainer.Qty.displayName = names.Qty;
CartLineContainer.Total.displayName = names.Total;
CartLineContainer.Remove.displayName = names.Remove;
CartLineContainer.Actions.displayName = names.Actions;
CartLineContainer.Notes.displayName = names.Notes;

export default CartLineContainer;
